import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/AppShell";

const IMG = "https://images.unsplash.com/photo-1600728619239-d2a73f7aa541?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const inputCls = "h-12 w-full rounded-lg border border-slate-300 bg-white px-4 outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-200";

export default function Login({ mode = "login" }) {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const isReg = mode === "register";
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(isReg ? "/auth/register" : "/auth/login", form);
      await signIn(data);
      nav("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-slate-900 lg:block">
        <img src={IMG} alt="Pedido de delivery lacrado" className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Brand className="h-12" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Defesa mais clara, decisão mais justa</p>
            <h2 className="mt-3 max-w-lg font-display text-5xl font-extrabold leading-[1.05] text-white">Transforme seus registros em um recurso que faz sentido.</h2>
            <p className="mt-5 max-w-md text-slate-300">Analise prints, fotos do pedido, lacres e comandas e receba o texto certo, no motivo certo, dentro do limite da plataforma.</p>
          </div>
          <p className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4" /> Dados privados por estabelecimento · Boas práticas LGPD</p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <img src="/logo-full.png" alt="Contestafood" data-testid="login-mobile-logo" className="mb-8 h-10 w-auto lg:hidden" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">{isReg ? "Criar conta" : "Acessar"}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-slate-900">{isReg ? "Cadastre seu restaurante" : "Bem-vindo de volta"}</h1>
          <p className="mt-2 text-sm text-slate-500">{isReg ? "Comece com 10 contestações grátis após completar seu cadastro." : "Entre para analisar e contestar ocorrências."}</p>

          <button data-testid="google-login-btn" onClick={google} className="mt-8 flex h-12 w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white font-semibold text-slate-800 transition-colors hover:bg-slate-100">
            <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continuar com Google
          </button>
          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />ou com e-mail<span className="h-px flex-1 bg-slate-200" /></div>

          <form onSubmit={submit} className="space-y-4">
            {isReg && <input data-testid="register-name-input" required placeholder="Nome do responsável" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
            <input data-testid="login-email-input" required type="email" placeholder="E-mail" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="login-password-input" required type="password" minLength={6} placeholder="Senha" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {!isReg && <div className="text-right"><Link data-testid="forgot-password-link" to="/esqueci-senha" className="text-sm font-semibold text-amber-700 hover:underline">Esqueci minha senha</Link></div>}
            <button data-testid="login-submit-btn" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-slate-900 font-bold text-white transition-colors hover:bg-slate-800 rounded-xl disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isReg ? "Criar conta" : "Entrar"}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            {isReg ? "Já tem conta? " : "Ainda não tem conta? "}
            <Link data-testid="toggle-auth-mode-link" to={isReg ? "/login" : "/cadastro"} className="font-semibold text-amber-700 hover:underline">{isReg ? "Entrar" : "Cadastre-se"}</Link>
          </p>
          <p className="mt-4 text-center text-xs text-slate-400">Ao continuar você concorda com a <Link to="/privacidade" data-testid="privacy-link" className="underline">Política de Privacidade</Link>.</p>
        </div>
      </div>
    </div>
  );
}
